import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { ChatMessage } from '../webrtc/types'
import { Button } from './ui/Button'

interface ChatPanelProps {
  messages: ChatMessage[]
  onSend: (text: string) => void
  onClose: () => void
  canClear?: boolean
  onClear?: () => void
}

export function ChatPanel({ messages, onSend, onClose, canClear, onClear }: ChatPanelProps) {
  const [text, setText] = useState('')
  const [confirmingClear, setConfirmingClear] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    onSend(text)
    setText('')
  }

  return (
    <aside className="chat-panel">
      <header className="chat-head">
        <strong>Chat</strong>
        <div className="chat-head-actions">
          {canClear && onClear ? (
            confirmingClear ? (
              <>
                <span className="chat-clear-confirm">Verlauf löschen?</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    onClear()
                    setConfirmingClear(false)
                  }}
                >
                  Ja
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmingClear(false)}>
                  Nein
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingClear(true)}
                aria-label="Chatverlauf löschen"
                title="Chatverlauf löschen"
              >
                🗑
              </Button>
            )
          ) : null}
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Chat schließen">
            ✕
          </Button>
        </div>
      </header>
      <div className="chat-body" ref={scrollRef}>
        {messages.length === 0 ? (
          <p className="muted">Noch keine Nachrichten.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`chat-msg ${m.self ? 'self' : ''}`}>
              <div className="chat-msg-head">
                <strong>{m.displayName}</strong>
                <time>{new Date(m.ts).toLocaleTimeString()}</time>
              </div>
              <div className="chat-msg-text">{m.text}</div>
            </div>
          ))
        )}
      </div>
      <form onSubmit={submit} className="chat-form">
        <input
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Nachricht…"
          maxLength={4000}
        />
        <Button type="submit">Senden</Button>
      </form>
    </aside>
  )
}
